import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '@/lib/api';
import { queryKeys } from '@/lib/query';
import type { HnfCustomer, HnfLink, HnfStatus } from '@/types/api';

/**
 * Hooks für die HNF-Mapping-DB-Bridge (WT-206, Sprint 2).
 *
 * `useCustomerHnfLink` ist 404-tolerant: kein Mapping = `data === null`,
 * kein Fehler. So bleibt die UI ohne Spinner-Springen wenn ein Kunde nicht
 * verknüpft ist.
 */

export function useHnfStatus(): ReturnType<typeof useQuery<HnfStatus>> {
  return useQuery<HnfStatus>({
    queryKey: queryKeys.hnf.status,
    queryFn: () => api.get<HnfStatus>('/integrations/hnf/status'),
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Backend-Wire-Modell (raw): der Server liefert {data:[...], meta} und statt
// zammad_organization_id eine generische identifiers-Liste.
// ---------------------------------------------------------------------------

interface WireHnfIdentifier {
  identifier_type: string;
  identifier_value: string;
}

interface WireHnfCustomer {
  id: number;
  name: string;
  kimai_customer_id?: number | null;
  identifiers?: WireHnfIdentifier[];
}

interface WireHnfCustomerList {
  data: WireHnfCustomer[];
  meta?: Record<string, unknown>;
}

/** Wire -> Frontend-Typ: zammad_organization_id aus identifiers ableiten. */
function mapWireHnfCustomer(wire: WireHnfCustomer): HnfCustomer {
  const identifiers = wire.identifiers ?? [];
  const zammad = identifiers.find((i) =>
    i.identifier_type.toLowerCase().includes('zammad')
  );
  const zammadId = zammad ? Number(zammad.identifier_value) : NaN;
  return {
    id: wire.id,
    name: wire.name,
    kimai_customer_id: wire.kimai_customer_id ?? null,
    zammad_organization_id: Number.isFinite(zammadId) ? zammadId : null,
    identifier_map: Object.fromEntries(
      identifiers.map((i) => [i.identifier_type, i.identifier_value])
    ),
  };
}

export function useHnfCustomers(
  search: string | undefined
): ReturnType<typeof useQuery<HnfCustomer[]>> {
  return useQuery<HnfCustomer[]>({
    queryKey: queryKeys.hnf.customers(search),
    queryFn: async () => {
      const res = await api.get<WireHnfCustomerList>('/integrations/hnf/customers', {
        'filter[search]': search,
      });
      return res.data.map(mapWireHnfCustomer);
    },
    staleTime: 5 * 60_000,
  });
}

export function useCustomerHnfLink(
  customerId: string | undefined
): ReturnType<typeof useQuery<HnfLink | null>> {
  return useQuery<HnfLink | null>({
    queryKey: queryKeys.customers.hnfLink(customerId ?? ''),
    queryFn: async () => {
      try {
        return await api.get<HnfLink>(`/customers/${customerId ?? ''}/hnf-link`);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    enabled: Boolean(customerId),
    retry: false,
  });
}

export interface LinkHnfInput {
  customerId: string;
  hnfCustomerId: number;
}

export function useLinkHnf(): ReturnType<typeof useMutation<HnfLink, Error, LinkHnfInput>> {
  const qc = useQueryClient();
  return useMutation<HnfLink, Error, LinkHnfInput>({
    mutationFn: ({ customerId, hnfCustomerId }) =>
      api.put<HnfLink, { hnf_customer_id: number }>(
        `/customers/${customerId}/hnf-link`,
        { hnf_customer_id: hnfCustomerId }
      ),
    onSuccess: (_link, vars) => {
      void qc.invalidateQueries({ queryKey: queryKeys.customers.hnfLink(vars.customerId) });
      void qc.invalidateQueries({ queryKey: queryKeys.hnf.status });
    },
  });
}

export function useUnlinkHnf(): ReturnType<typeof useMutation<void, Error, string>> {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (customerId) => api.delete<void>(`/customers/${customerId}/hnf-link`),
    onSuccess: (_void, customerId) => {
      void qc.invalidateQueries({ queryKey: queryKeys.customers.hnfLink(customerId) });
      void qc.invalidateQueries({ queryKey: queryKeys.hnf.status });
    },
  });
}
